import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

export default function PublicGames() {
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [tournament, setTournament] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/games"), api.get("/teams"), api.get("/tournaments")]).then(([g, te, t]) => {
      setGames(g.data);
      setTeams(Object.fromEntries(te.data.map((x) => [x.id, x])));
      setTournaments(t.data);
    });
  }, []);

  const selectedTournament = tournaments.find((tt) => tt.id === tournament);
  const categoryOptions = selectedTournament?.categories || [];

  useEffect(() => { setCategory("all"); }, [tournament]);

  const filtered = useMemo(() => {
    return games.filter((g) => {
      if (status !== "all" && g.status !== status) return false;
      if (tournament !== "all" && g.tournament_id !== tournament) return false;
      if (category !== "all") {
        const a = teams[g.team_a_id];
        const b = teams[g.team_b_id];
        if (a?.category_id !== category && b?.category_id !== category) return false;
      }
      if (q) {
        const a = teams[g.team_a_id]?.name || "";
        const b = teams[g.team_b_id]?.name || "";
        if (!`${a} ${b}`.toLowerCase().includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [games, q, status, tournament, category, teams]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <PageHeader title={t("games.title")} subtitle={t("games.subtitle")} testid="page-games" />

      <div className={`grid ${categoryOptions.length > 0 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 mb-8`}>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input
            data-testid="games-search-input"
            placeholder={t("nav.teams")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-navy-deep border-navy-border text-white rounded-sm"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="games-status-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">{t("games.filter_all")}</SelectItem>
            <SelectItem value="live">{t("games.filter_live")}</SelectItem>
            <SelectItem value="overtime">{t("game.overtime")}</SelectItem>
            <SelectItem value="scheduled">{t("games.filter_upcoming")}</SelectItem>
            <SelectItem value="finished">{t("games.filter_finished")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tournament} onValueChange={setTournament}>
          <SelectTrigger data-testid="games-tournament-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">{t("games.filter_tournament_all")}</SelectItem>
            {tournaments.map((tour) => <SelectItem key={tour.id} value={tour.id}>{tour.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {categoryOptions.length > 0 && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="games-category-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-navy-deep border-navy-border text-white">
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categoryOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="border border-navy-border rounded-sm overflow-hidden divide-y divide-navy-border">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-ink-muted">{t("games.empty")}</div>
        ) : filtered.map((g) => {
          const a = teams[g.team_a_id];
          const b = teams[g.team_b_id];
          const winA = g.status === "finished" && g.score_a > g.score_b;
          const winB = g.status === "finished" && g.score_b > g.score_a;
          return (
            <Link key={g.id} to={`/games/${g.id}`} data-testid={`games-row-${g.id}`} className="grid grid-cols-12 items-center gap-2 px-4 py-4 bg-navy-deep hover:bg-navy-surface/40 transition-colors">
              <div className="col-span-2 text-xs font-mono tabular text-ink-muted uppercase">
                <div>{g.date}</div>
                <div className="text-[10px]">{g.time || ""}</div>
              </div>
              <div className="col-span-7 grid grid-cols-2 gap-4">
                <div className={`flex items-center gap-3 ${winA ? "text-orange" : "text-white"}`}>
                  <Logo team={a} />
                  <span className="font-heading uppercase tracking-tight truncate">{a?.name || "TBD"}</span>
                </div>
                <div className={`flex items-center gap-3 ${winB ? "text-orange" : "text-white"}`}>
                  <Logo team={b} />
                  <span className="font-heading uppercase tracking-tight truncate">{b?.name || "TBD"}</span>
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-3 font-mono tabular text-2xl">
                <span className={winA ? "text-orange font-bold" : "text-ink-secondary"}>{g.score_a}</span>
                <span className="text-ink-muted">–</span>
                <span className={winB ? "text-orange font-bold" : "text-ink-secondary"}>{g.score_b}</span>
              </div>
              <div className="col-span-1 text-right">
                <StatusPill status={g.status} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Logo({ team }) {
  return (
    <div className="w-8 h-8 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
      {team?.logo_url ? (
        <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="font-heading font-black text-orange text-sm">{team?.name?.[0] || "?"}</span>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const m = {
    live: "bg-court-red text-white animate-pulse",
    overtime: "bg-orange text-white",
    finished: "bg-navy-surface text-ink-secondary",
    scheduled: "bg-transparent border border-navy-border text-ink-secondary",
  };
  return <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-widest rounded-sm ${m[status] || m.scheduled}`}>{status}</span>;
}
